import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, FolderOpen } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { useProjectStore } from '@/store/projectStore'
import { useDockUiStore, MINIMISED_RAIL_WIDTH } from '@/store/dockUiStore'
import AskAIDock from '@/components/agent/AskAIDock'
import DockErrorBoundary from '@/components/agent/DockErrorBoundary'
import OnboardingHelpChat from '@/components/common/OnboardingHelpChat'
import ProjectsModal from './ProjectsModal'
import logoSmall from '@/assets/logo-small.png'
import logoLarge from '@/assets/logo-large.png'

interface SidebarProps {
  /** True when rendered as a mobile overlay drawer (always shown expanded). */
  mobile?: boolean
  onNavigate?: () => void
}

// ---------------------------------------------------------------------------
// Left rail. Hosts the Twasta brand, the "New Application" + "Open project"
// actions, and the persistent Ask-AI agent panel (which replaced the inline
// project list — projects now open from ProjectsModal). The rail's width and
// view state (expanded / minimised / maximized) come from useDockUiStore so the
// agent dock and the rail stay in sync.
// ---------------------------------------------------------------------------

export default function Sidebar({ mobile = false, onNavigate }: SidebarProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentProject, projects, fetchProjects } = useProjectStore()
  const { panelOpen, maximized, width, setProjectModalOpen, setWidth } = useDockUiStore()

  const asideRef = useRef<HTMLElement>(null)

  // Keep the project list warm so the empty-state can tell the user whether
  // they have existing projects to open (and the modal opens instantly).
  useEffect(() => {
    if (user) fetchProjects()
  }, [user, fetchProjects])

  // On mobile the drawer is always shown expanded; on desktop the rail follows
  // the store (minimised → thin vertical rail).
  const expanded = mobile ? true : panelOpen
  // Projects shared with this user (owned by others) — surfaced as a badge on
  // the "Open project" button so collaboration is discoverable.
  const sharedCount = projects.filter((p) => p.access && !p.access.is_owner).length

  const handleNewApplication = useCallback(() => {
    navigate('/builder')
    onNavigate?.()
  }, [navigate, onNavigate])

  const openProjects = useCallback(() => {
    setProjectModalOpen(true)
  }, [setProjectModalOpen])

  // Drag-to-resize the expanded rail. Active only on desktop while expanded and
  // not maximized. Width is clamped + persisted inside the store.
  const startResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const startLeft = asideRef.current?.getBoundingClientRect().left ?? 0
      const onMove = (ev: MouseEvent) => setWidth(ev.clientX - startLeft)
      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [setWidth],
  )

  // Width: maximized rail flexes to fill the row (AppLayout hides main); mobile
  // width is fixed by CSS; otherwise expanded → resizable width, minimised → thin.
  const asideStyle: React.CSSProperties = maximized && !mobile
    ? { flex: 1, minWidth: 0 }
    : { width: mobile ? 280 : expanded ? width : MINIMISED_RAIL_WIDTH, flexShrink: 0 }

  const showResize = !mobile && expanded && !maximized

  // Responsive collapse for the expanded action buttons — they now sit on one
  // row. At the default rail width (380px) BOTH labels are visible; only as the
  // rail is dragged narrower than the default does the secondary "Open project"
  // yield to an icon first, then the primary "New Application" too. Driven off
  // the rail's own width (the resizable store value) so it reacts to the drag.
  const railWidth = mobile ? 280 : width
  const showOpenText = railWidth >= 368
  // Once "Open project" is an icon (~36px) there's room for the "New
  // Application" label all the way down to the minimum rail width, so it only
  // collapses at an extreme narrowness that the resizable range rarely reaches.
  const showNewText = railWidth >= 250

  return (
    <aside
      ref={asideRef}
      className={`sidebar ${expanded ? '' : 'sidebar--collapsed'}`}
      style={{
        ...asideStyle,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-bg-secondary)',
        transition: maximized ? 'none' : 'width 200ms ease',
        overflow: 'hidden',
        position: 'relative',
        borderRight: '1px solid var(--color-border)',
      }}
    >
      {/* Brand header — hidden while the agent is maximized so the panel truly
          owns the full screen (Restore / Esc brings it back). */}
      {!(maximized && !mobile) && (
        <div
          style={{
            // Slightly tighter bottom padding so the action row sits closer to
            // the logo.
            padding: expanded ? '1rem 1.25rem 0.75rem' : '1rem 0 0.75rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: expanded ? 'flex-start' : 'center',
            gap: '0.5rem',
            minHeight: 52,
            flexShrink: 0,
          }}
          onClick={() => navigate('/workbench')}
        >
          {expanded ? (
            <img src={logoLarge} alt="Twasta.ai" style={{ height: 28, objectFit: 'contain' }} />
          ) : (
            <img src={logoSmall} alt="Twasta.ai" style={{ width: 32, height: 32, flexShrink: 0 }} />
          )}
        </div>
      )}

      {/* Actions — New Application + Open project. Twasta logo stays above;
          the agent panel fills the space below. Hidden while maximized so the
          agent gets the full viewport. */}
      {!maximized && (expanded ? (
        <div
          style={{
            padding: '0.5rem 0.75rem 0.75rem',
            display: 'flex',
            flexDirection: 'row',
            gap: '0.5rem',
            flexShrink: 0,
          }}
        >
          <button
            className="btn btn-primary btn-sm"
            onClick={handleNewApplication}
            title="New Application"
            style={{
              flex: showNewText ? 1 : '0 0 auto',
              width: showNewText ? undefined : 36,
              height: 36,
              padding: showNewText ? undefined : 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
            }}
          >
            <Plus size={16} />
            {showNewText && 'New Application'}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={openProjects}
            title={sharedCount > 0 ? `Open project · ${sharedCount} shared with you` : 'Open project'}
            style={{
              flex: showOpenText ? 1 : '0 0 auto',
              width: showOpenText ? undefined : 36,
              height: 36,
              padding: showOpenText ? undefined : 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              position: 'relative',
            }}
          >
            <FolderOpen size={16} />
            {showOpenText && 'Open project'}
            {sharedCount > 0 && (showOpenText ? (
              <span
                title={`${sharedCount} shared with you`}
                style={{
                  marginLeft: 2,
                  minWidth: 18,
                  height: 18,
                  padding: '0 5px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-accent)',
                  color: '#fff',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {sharedCount}
              </span>
            ) : (
              <span
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-accent)',
                  color: '#fff',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--color-bg-secondary)',
                }}
              >
                {sharedCount}
              </span>
            ))}
          </button>
        </div>
      ) : (
        <div
          style={{
            padding: '0.75rem 0.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            flexShrink: 0,
          }}
        >
          <button
            className="btn btn-primary btn-icon"
            onClick={handleNewApplication}
            title="New Application"
            style={{ width: 36, height: 36 }}
          >
            <Plus size={16} />
          </button>
          <button
            className="btn btn-secondary btn-icon"
            onClick={openProjects}
            title={sharedCount > 0 ? `Open project · ${sharedCount} shared with you` : 'Open project'}
            style={{ width: 36, height: 36, position: 'relative' }}
          >
            <FolderOpen size={16} />
            {sharedCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-accent)',
                  color: '#fff',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--color-bg-secondary)',
                }}
              >
                {sharedCount}
              </span>
            )}
          </button>
        </div>
      ))}

      {/* Agent panel — fills the rest of the rail. AskAIDock decides whether to
          render its expanded panel or the minimised vertical strip based on the
          shared store state. With NO project open the dock can't run (it is
          project-scoped), so the rail hosts the read-only "ask about Twasta"
          assistant instead — same place, same input, but it answers questions
          rather than editing an app. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {currentProject ? (
          <DockErrorBoundary project={currentProject.application.folder_name}>
            <AskAIDock />
          </DockErrorBoundary>
        ) : (
          expanded && (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                padding: '1rem 0.875rem',
                borderTop: '1px solid var(--color-border)',
              }}
            >
              <OnboardingHelpChat variant="panel" />
            </div>
          )
        )}
      </div>

      {/* Resize handle on the right edge. */}
      {showResize && (
        <div
          onMouseDown={startResize}
          title="Drag to resize"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 6,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 5,
          }}
        />
      )}

      <ProjectsModal />
    </aside>
  )
}
